import { SectionConfig } from '@core/models/store-config.model';

/**
 * Parámetros que el admin guarda en `section.content.settings`
 * (ver faisen-admin-web/src/features/ecommerce/section-settings.registry.ts).
 * Los defaults de aquí deben coincidir con los del registro del admin.
 */
/** Mismo texto que DEFAULT_ABOUT_TEXT del admin (ecommerce-store.constants.ts). */
export const DEFAULT_ABOUT_TEXT = 'Tienda oficial en la plataforma FAISEN con productos, servicios y combos de calidad.';

export function settingsOf<T extends object>(section: SectionConfig | undefined, defaults: T): T {
  const saved = (section?.content?.['settings'] || {}) as Partial<T>;
  const out = { ...defaults };
  for (const key of Object.keys(defaults) as (keyof T)[]) {
    const v = saved[key];
    if (v !== undefined && v !== null) out[key] = v as T[keyof T];
  }
  return out;
}

/** Fecha local YYYY-MM-DD, mismo formato que guarda el `<input type="date">` del admin. */
export function todayIso(now = new Date()): string {
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

/** Fechas vacías = sin límite. Ambos extremos incluidos. */
export function isWithinDates(start: string | undefined, end: string | undefined, today = todayIso()): boolean {
  return (!start || start <= today) && (!end || end >= today);
}

export type DeviceVisibility = 'all' | 'desktop' | 'mobile';

/** Clases Tailwind para "Mostrar en" (breakpoint md, el mismo del resto de la tienda). */
export function deviceClass(device: DeviceVisibility | undefined): string {
  if (device === 'desktop') return 'hidden md:block';
  if (device === 'mobile') return 'md:hidden';
  return '';
}

export interface HeroSettings {
  allowCta: boolean;
  fallbackMode: 'default' | 'hide';
  layout: 'split' | 'background';
  height: 'sm' | 'md' | 'lg' | 'full';
  width: 'contained' | 'full';
  textAlign: 'left' | 'center';
  textTheme: 'dark' | 'light';
  maxSlides: number;
  autoplay: boolean;
  intervalSec: number;
  pauseOnHover: boolean;
  loop: boolean;
  transition: 'fade' | 'slide';
  showArrows: boolean;
  showDots: boolean;
}

export const HERO_DEFAULTS: HeroSettings = {
  allowCta: true,
  fallbackMode: 'default',
  layout: 'background',
  height: 'md',
  width: 'contained',
  textAlign: 'left',
  textTheme: 'dark',
  maxSlides: 5,
  autoplay: true,
  intervalSec: 5,
  pauseOnHover: true,
  loop: true,
  transition: 'fade',
  showArrows: true,
  showDots: true,
};

// ── Helpers de estilo comunes ─────────────────────────────────────────────────

/** "Ancho" del admin: contenido con márgenes o de borde a borde. */
export function containerClass(width: 'contained' | 'full' | undefined): string {
  return width === 'full' ? 'px-4 md:px-8' : 'max-w-7xl mx-auto px-4';
}

/** "Fondo" del admin. */
export function backgroundClass(bg: 'card' | 'muted' | 'none' | undefined): string {
  if (bg === 'card') return 'bg-white rounded-xl border border-gray-100 shadow-xs p-2 sm:p-2.5';
  if (bg === 'muted') return 'bg-gray-50 rounded-xl p-2 sm:p-2.5';
  return '';
}

/**
 * Columnas configurables sin generar una clase por número: las variables CSS
 * alimentan `GRID_COLS_CLASS` (clase fija que Tailwind sí ve en el código).
 */
export function gridVars(desktop: number, mobile: number): Record<string, string> {
  const d = Math.max(1, desktop);
  // Tablet: como mucho 4 columnas, para que con 6 en escritorio las tarjetas no queden diminutas.
  return { '--cols-d': String(d), '--cols-t': String(Math.min(d, 4)), '--cols-m': String(Math.max(1, mobile)) };
}
export const GRID_COLS_CLASS =
  'grid gap-3 lg:gap-4 grid-cols-[repeat(var(--cols-m),minmax(0,1fr))] md:grid-cols-[repeat(var(--cols-t),minmax(0,1fr))] lg:grid-cols-[repeat(var(--cols-d),minmax(0,1fr))]';

export interface CarouselSettings {
  autoplay: boolean;
  intervalSec: number;
  showArrows: boolean;
}

export interface HeaderSettings {
  showTitle: boolean;
  showViewAll: boolean;
  viewAllText: string;
  viewAllAction: { type?: string; value?: string; targetSlug?: string };
}

// ── Marcas ───────────────────────────────────────────────────────────────────

export interface BrandsSettings {
  showTitle: boolean;
  source: 'all' | 'manual';
  selectedBrands: { id: string; name: string; slug?: string }[];
  limit: number;
  onlyWithLogo: boolean;
  layout: 'strip' | 'grid' | 'marquee';
  columnsDesktop: number;
  columnsMobile: number;
  itemStyle: 'logo-name' | 'logo' | 'name';
  logoSize: 'sm' | 'md' | 'lg';
  grayscale: boolean;
  background: 'card' | 'muted' | 'none';
  width: 'contained' | 'full';
  marqueeSpeed: 'slow' | 'normal' | 'fast';
  pauseOnHover: boolean;
  clickAction: 'filter' | 'none';
}

export const BRANDS_DEFAULTS: BrandsSettings = {
  showTitle: true,
  source: 'all',
  selectedBrands: [],
  limit: 12,
  onlyWithLogo: false,
  layout: 'strip',
  columnsDesktop: 6,
  columnsMobile: 3,
  itemStyle: 'logo-name',
  logoSize: 'md',
  grayscale: false,
  background: 'card',
  width: 'contained',
  marqueeSpeed: 'normal',
  pauseOnHover: true,
  clickAction: 'filter',
};

// ── Categorías ───────────────────────────────────────────────────────────────

export interface CategoriesSettings extends HeaderSettings, CarouselSettings {
  source: 'parents' | 'all' | 'manual';
  selectedCategories: { id: string; name: string; slug?: string }[];
  limit: number;
  hideEmpty: boolean;
  layout: 'grid' | 'carousel';
  columnsDesktop: number;
  columnsMobile: number;
  cardStyle: 'image' | 'circle' | 'text';
  imageRatio: 'square' | 'landscape' | 'portrait';
  showProductCount: boolean;
  showDescription: boolean;
  background: 'card' | 'muted' | 'none';
  width: 'contained' | 'full';
}

export const CATEGORIES_DEFAULTS: CategoriesSettings = {
  showTitle: true,
  source: 'parents',
  selectedCategories: [],
  limit: 6,
  hideEmpty: true,
  showViewAll: true,
  viewAllText: 'Ver todas',
  viewAllAction: { type: 'PAGE', value: '/categorias' },
  layout: 'grid',
  columnsDesktop: 6,
  columnsMobile: 3,
  cardStyle: 'image',
  imageRatio: 'square',
  showProductCount: true,
  showDescription: false,
  background: 'none',
  width: 'contained',
  autoplay: false,
  intervalSec: 5,
  showArrows: true,
};

// ── Listas de productos (FEATURED, BEST_SELLERS, NEW_ARRIVALS, PROMO_KITS, SERVICES) ──

export interface ProductListSettings extends HeaderSettings, CarouselSettings {
  badgeText: string;
  source: 'auto' | 'category' | 'brand' | 'manual';
  sourceCategory: { id: string; name: string; slug?: string }[];
  sourceBrand: { id: string; name: string; slug?: string }[];
  selectedItems: { id: string; name: string; slug?: string }[];
  sort: 'relevance' | 'newest' | 'popularity' | 'price-asc' | 'price-desc';
  limit: number;
  onlyInStock: boolean;
  hideIfEmpty: boolean;
  layout: 'grid' | 'carousel';
  columnsDesktop: number;
  columnsMobile: number;
  showPrice: boolean;
  showPreviousPrice: boolean;
  showDiscountBadge: boolean;
  showBrand: boolean;
  showCategory: boolean;
  showRating: boolean;
  showStock: boolean;
  showAddToCart: boolean;
  showWishlist: boolean;
  background: 'card' | 'muted' | 'none';
  width: 'contained' | 'full';
  // Específicos por tipo (se ignoran donde no aplican)
  showRankBadge: boolean;
  daysRange: number;
  showNewBadge: boolean;
  showKitItems: boolean;
  showSavings: boolean;
  showDuration: boolean;
  showModality: boolean;
  ctaMode: 'cart' | 'whatsapp' | 'detail';
}

const PRODUCT_LIST_BASE: ProductListSettings = {
  showTitle: true,
  badgeText: '',
  source: 'auto',
  sourceCategory: [],
  sourceBrand: [],
  selectedItems: [],
  sort: 'relevance',
  // Estilo compacto (tipo Amazon): una fila de 6 por sección; "Ver todos" lleva al resto.
  limit: 6,
  onlyInStock: false,
  hideIfEmpty: true,
  showViewAll: true,
  viewAllText: 'Ver todos',
  viewAllAction: { type: 'PAGE', value: '/productos' },
  layout: 'grid',
  columnsDesktop: 6,
  columnsMobile: 2,
  showPrice: true,
  showPreviousPrice: true,
  showDiscountBadge: true,
  showBrand: true,
  showCategory: false,
  showRating: true,
  showStock: false,
  showAddToCart: true,
  showWishlist: true,
  background: 'none',
  width: 'contained',
  autoplay: false,
  intervalSec: 5,
  showArrows: true,
  showRankBadge: true,
  daysRange: 0,
  showNewBadge: true,
  showKitItems: true,
  showSavings: true,
  showDuration: true,
  showModality: true,
  ctaMode: 'cart',
};

/** Mismos defaults por tipo que `productListFields(...)` en el registro del admin. */
export const PRODUCT_LIST_DEFAULTS: Record<string, ProductListSettings> = {
  FEATURED_PRODUCTS: { ...PRODUCT_LIST_BASE },
  BEST_SELLERS: { ...PRODUCT_LIST_BASE, sort: 'popularity' },
  NEW_ARRIVALS: { ...PRODUCT_LIST_BASE, sort: 'newest', viewAllAction: { type: 'INTERNAL_URL', value: '/productos?ordenar=newest' } },
  // Sus tarjetas muestran más (qué incluye, duración): 4 por fila.
  PROMO_KITS: { ...PRODUCT_LIST_BASE, limit: 4, columnsDesktop: 4, viewAllAction: { type: 'INTERNAL_URL', value: '/productos?tipo=KIT' } },
  SERVICES: { ...PRODUCT_LIST_BASE, limit: 4, columnsDesktop: 4, viewAllAction: { type: 'INTERNAL_URL', value: '/productos?tipo=SERVICE' } },
};
